import { useState, useEffect } from 'react';
import InputMetrics from './Form/InputMetrics';
import InputMetricsMultiple from './Form/InputMetricsMultiple';
import { MetricContainer, Button } from './styled';
import { useSelector, useDispatch } from 'react-redux';
import valuationSlice, {
  valuationSelectors,
  valuationEffects,
} from 'features/valuation/slice';
import valuationApi from 'lib/api/valuation';
import usePreviousState from 'hooks/usePreviousState';

function DropdownMetric() {
  const dispatch = useDispatch();
  const {
    valuationSymbol,
    valuationDefaultMetric,
    valuationResult,
    valuationDetail,
  } = useSelector(valuationSelectors);

  const { createValuation } = valuationEffects;

  const { setValuationMetric } = valuationSlice.actions;

  // Form values
  const [selectedEPS, setSelectedEPS] = useState<any>(0);
  const [valueEPS, setValueEPS] = useState<any>('');
  const [selectedGrowth, setSelectedGrowth] = useState<any>('CUSTOM');
  const [valueGrowth, setValueGrowth] = useState<any>('');
  const [selectedMultiple, setSelectedMultiple] = useState<any>(0);
  const [valueMultiple, setValueMultiple] = useState<any>('');

  // Data extracted from selector
  const { data: defaultMetrics } = valuationDefaultMetric;
  const {
    data: { symbol: resultSymbol },
  } = valuationResult;
  const { symbol: companySymbol } = valuationSymbol;
  const { data: valuationData } = valuationDetail;

  const setGrowthId = (growthId) => {
    return growthId === '' || growthId === 0 ? 'CUSTOM' : String(growthId);
  };

  useEffect(() => {
    if (defaultMetrics.length > 0) {
      const [eps, growth, multiple] = defaultMetrics;

      setSelectedEPS(String(eps.id));
      setValueEPS(eps.value);

      setSelectedGrowth(setGrowthId(growth.id));
      setValueGrowth(growth.value);

      setSelectedMultiple(String(multiple.id));
      setValueMultiple(multiple.value);
    }
  }, [defaultMetrics]);

  useEffect(() => {
    if (!resultSymbol && !companySymbol) {
      setSelectedEPS(0);
      setValueEPS('');

      setSelectedGrowth('CUSTOM');
      setValueGrowth('');

      setSelectedMultiple(0);
      setValueMultiple('');
    }
  }, [resultSymbol, companySymbol]);

  const preValuation = usePreviousState(valuationData.valuation);

  useEffect(() => {
    if (preValuation?.id !== valuationData.valuation?.id) {
      if (valuationData.metric) {
        if (valuationData.metric.item.length > 0) {
          const [eps, growth, multiple] = valuationData.metric.item;

          setSelectedEPS(String(eps.itemid));
          setValueEPS(eps.value);

          setSelectedGrowth(setGrowthId(growth.itemid));
          setValueGrowth(growth.value);

          setSelectedMultiple(String(multiple.itemid));
          setValueMultiple(multiple.value);
        }
      }
    }
  }, [valuationData.valuation]);

  const getMetricsData = async (id: number) => {
    if (!valuationSymbol.symbol) {
      return '';
    }

    const { symbol } = valuationSymbol;

    const res = await valuationApi.getValuationMetricPerID(symbol, id);

    if (!res.data) {
      return '';
    }

    const { error, data } = res.data;

    if (error) {
      return '';
    }

    return data[0];
  };

  const handleSelectEPS = (value) => {
    setSelectedEPS(value);

    // if select id same with default, change value to default
    if (Number(value) === Number(defaultMetrics[0].id)) {
      setValueEPS(defaultMetrics[0].value);
    } else {
      setValueEPS('');
    }
  };

  const handleChangeEPS = (e) => {
    const inputedEPS = e.target.value.trim();

    setValueEPS(inputedEPS);

    // if inputed same with default, change select to custom
    if (inputedEPS !== defaultMetrics[0].value) {
      setSelectedEPS('CUSTOM');
    }
  };

  const handleSelectGrowth = async (value) => {
    setSelectedGrowth(value);

    // if select id same with default, change value to default
    if (
      value === defaultMetrics[1].id ||
      (value === false && defaultMetrics[1].id === '')
    ) {
      setValueGrowth(defaultMetrics[1].value);
      return;
    }

    if (value !== 'CUSTOM') {
      const data = await getMetricsData(value);
      setValueGrowth(data?.value || '');
      return;
    }

    setValueGrowth('');
  };

  const handleChangeGrowth = (e) => {
    const inputedGrowth = e.target.value.trim();

    setValueGrowth(inputedGrowth);

    // if inputed same with default, change select to custom
    if (inputedGrowth !== defaultMetrics[1].value) {
      setSelectedGrowth('CUSTOM');
    }
  };

  const handleSelectMultiple = async (value) => {
    setSelectedMultiple(value);

    // if select id same with default, change value to default
    if (value === defaultMetrics[2].id) {
      setValueMultiple(defaultMetrics[2].value);
      return;
    }

    if (value !== 'CUSTOM') {
      const data = await getMetricsData(value);
      setValueMultiple(data?.value || '');
      return;
    }
    setValueMultiple('');
  };

  const handleChangeMultiple = (e) => {
    const inputedMultiple = e.target.value.trim();

    setValueMultiple(inputedMultiple);

    // if inputed same with default, change select to custom
    if (inputedMultiple !== defaultMetrics[2].value) {
      setSelectedMultiple('CUSTOM');
    }
  };

  const calculateValue = () => {
    const { id } = valuationSymbol;
    const payload = {
      symbol: valuationSymbol.symbol,
      eps_value: valueEPS,
      growth_value: valueGrowth,
      multiple_value: valueMultiple,
    };

    const payloadMetric = {
      comp_id: id,
      item: [
        {
          name: 'EPS',
          itemid: String(selectedEPS),
          value: valueEPS,
        },
        {
          name: 'GROWTH',
          itemid: String(selectedGrowth),
          value: valueGrowth,
        },
        {
          name: 'MULTIPLE',
          itemid: String(selectedMultiple),
          value: valueMultiple,
        },
      ],
    };

    if (valuationSymbol.symbol) {
      dispatch(createValuation(payload));
      dispatch(setValuationMetric(payloadMetric));
    }
  };

  const disabled = !valuationSymbol.symbol;

  return (
    <>
      <MetricContainer>
        <InputMetrics
          metrics="eps"
          selectValue={selectedEPS}
          value={valueEPS}
          onChange={handleChangeEPS}
          onSelect={handleSelectEPS}
          disabled={disabled}
        />
        <InputMetrics
          metrics="growth"
          selectValue={selectedGrowth}
          value={valueGrowth}
          onChange={handleChangeGrowth}
          onSelect={handleSelectGrowth}
          disabled={disabled}
        />
        <InputMetricsMultiple
          selectValue={selectedMultiple}
          value={valueMultiple}
          onChange={handleChangeMultiple}
          onSelect={handleSelectMultiple}
          disabled={disabled}
        />
      </MetricContainer>
      <Button
        width="65px"
        htmlType="submit"
        type="primary"
        onClick={calculateValue}
      >
        Value
      </Button>
    </>
  );
}

export default DropdownMetric;
